import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play,
  Square,
  Plus,
  Search,
  Filter,
  Edit3,
  Trash2,
  Clock,
  CheckSquare,
  X,
  XCircle,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  List,
  Sparkles,
  Loader2,
  Check,
  Scissors,
} from "lucide-react";
import {
  timeEntriesApi,
  mattersApi,
  usersApi,
  clientsApi,
  PaginatedResponse,
} from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import TimeCalendarView from "../billing/TimeCalendarView";
import AiMatterSuggestion from "../ui/AiMatterSuggestion";
import SplitEntryModal from "./SplitEntryModal";
import ScreenCaptureWidget from "../time/ScreenCaptureWidget";
import { useRunningTimer } from "../../contexts/RunningTimerContext";
import { useUserDataContext } from "../../context/UserDataContext";
import { notifyError, notifySuccess } from "../ui/Toast";

interface TimeEntry {
  _id: string;
  matterId: any;
  clientId: any;
  userId: any;
  date: string;
  minutes: number;
  narrative: string;
  billable: boolean;
  rateCents: number;
  amountCents: number;
  status: string;
  startTime?: string | null;
  timer?: { startedAt: string; stoppedAt: string | null; isRunning: boolean };
}

interface Props {
  onDataChange?: () => void;
}

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

/** YYYY-MM-DD in the user's local time zone (toISOString would give the UTC date). */
const toLocalDateString = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Total minutes from a decimal hours string (e.g. "1.3") plus extra minutes. */
const toTotalMinutes = (hours: string, minutes: number) => {
  const h = parseFloat(hours);
  return Math.round((Number.isFinite(h) ? h : 0) * 60) + (minutes || 0);
};

const formatMinutes = (mins: number) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const statusColors: Record<string, string> = {
  suggested: "bg-blue-100 text-blue-700",
  review: "bg-purple-100 text-purple-700",
  unbilled: "bg-amber-100 text-amber-700",
  billed: "bg-emerald-100 text-emerald-700",
  written_off: "bg-slate-100 text-slate-500",
};

const TrackTimeTab: React.FC<Props> = ({ onDataChange }) => {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [matters, setMatters] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);

  // Filters
  const [filterMatter, setFilterMatter] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  // Timer state is shared with the floating timer via RunningTimerContext
  const {
    runningTimer,
    elapsedSeconds: timerElapsed,
    startTimer,
    stopTimer,
    loading: timerBusy,
  } = useRunningTimer();
  const { userData } = useUserDataContext();
  const currentUserId = userData?._id || "";

  // Create form
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    matterId: "",
    clientId: "",
    userId: "",
    date: toLocalDateString(new Date()),
    minutes: 0,
    hours: "",
    narrative: "",
    billable: true,
    rateCents: 0,
  });

  // Edit state
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [editForm, setEditForm] = useState({ narrative: "", minutes: 0, rateCents: 0 });

  // Bulk select
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Timer widget
  const [timerMatter, setTimerMatter] = useState("");
  const [timerNarrative, setTimerNarrative] = useState("");
  const [timerRate, setTimerRate] = useState(0);

  // View mode
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");
  const [selectedCalendarEntry, setSelectedCalendarEntry] = useState<TimeEntry | null>(null);

  // AI Improve Narrative state
  const [improvingNarrative, setImprovingNarrative] = useState(false);
  const [narrativeImprovement, setNarrativeImprovement] = useState<{
    improved: string;
    changes: string[];
  } | null>(null);

  // Split entry state
  const [splittingEntry, setSplittingEntry] = useState<TimeEntry | null>(null);

  const handleSplitEntry = (entry: TimeEntry) => {
    setSplittingEntry(entry);
  };

  // Weekly timesheet
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const d = new Date();
    const day = d.getDay(); // 0=Sun
    const diff = day === 0 ? -6 : 1 - day; // start on Monday
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [weekEntries, setWeekEntries] = useState<TimeEntry[]>([]);
  const [loadingWeek, setLoadingWeek] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page: pagination.page, limit: pagination.limit };
      if (filterMatter) params.matterId = filterMatter;
      if (filterUser) params.userId = filterUser;
      if (filterStatus) params.status = filterStatus;
      if (filterDateFrom) params.dateFrom = filterDateFrom;
      if (filterDateTo) params.dateTo = filterDateTo;

      const res = await timeEntriesApi.list(params);
      const data = res.data as PaginatedResponse<TimeEntry>;
      setEntries(data.data);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to fetch time entries:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filterMatter, filterUser, filterStatus, filterDateFrom, filterDateTo]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const fetchWeekEntries = useCallback(async () => {
    setLoadingWeek(true);
    try {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const res = await timeEntriesApi.list({
        dateFrom: toLocalDateString(weekStart),
        dateTo: toLocalDateString(weekEnd),
        limit: 500,
      });
      setWeekEntries(res.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch week entries:", err);
    } finally {
      setLoadingWeek(false);
    }
  }, [weekStart]);

  useEffect(() => {
    fetchWeekEntries();
  }, [fetchWeekEntries]);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const minutesForDay = (day: Date) => {
    const key = toLocalDateString(day);
    return weekEntries
      .filter((e) => e.date.split("T")[0] === key)
      .reduce((sum, e) => sum + e.minutes, 0);
  };

  const totalWeekMinutes = weekEntries.reduce((sum, e) => sum + e.minutes, 0);

  const openAddForDay = (day: Date) => {
    setFormData({
      matterId: "",
      clientId: "",
      userId: currentUserId,
      date: toLocalDateString(day),
      minutes: 0,
      hours: "",
      narrative: "",
      billable: true,
      rateCents: 25000,
    });
    setShowCreateModal(true);
  };

  useEffect(() => {
    const loadFormData = async () => {
      try {
        const [mRes, uRes, cRes] = await Promise.all([
          mattersApi.list({ limit: 200, status: "open" }),
          usersApi.list({ limit: 200 }),
          clientsApi.list({ limit: 200 }),
        ]);
        setMatters(mRes.data?.data || []);
        setUsers(uRes.data?.data || []);
        setClients(cRes.data?.data || []);
      } catch (err) {
        console.error("Failed to load form data:", err);
      }
    };
    loadFormData();
  }, []);

  // When the shared timer starts or stops anywhere (this tab or the floating
  // timer), refresh the lists so the entry shows up with its final minutes.
  const prevRunningIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const id = runningTimer?._id || null;
    if (prevRunningIdRef.current !== undefined && prevRunningIdRef.current !== id) {
      fetchEntries();
      fetchWeekEntries();
      onDataChange?.();
    }
    prevRunningIdRef.current = id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningTimer?._id]);

  const formatTimerDisplay = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const handleStartTimer = async () => {
    if (!timerMatter) return;
    try {
      const matter = matters.find((m: any) => m._id === timerMatter);
      const clientId = typeof matter?.clientId === "object" ? matter.clientId._id : matter?.clientId;

      // The context starts the timer for the logged-in user
      await startTimer({
        matterId: timerMatter,
        clientId: clientId || null,
        narrative: timerNarrative || "Timer in progress",
        rateCents: timerRate || 25000,
        billable: true,
      });
      setTimerNarrative("");
    } catch (err) {
      notifyError("Couldn't start the timer.", err);
    }
  };

  const handleStopTimer = async () => {
    if (!runningTimer) return;
    try {
      await stopTimer();
    } catch (err) {
      notifyError("Couldn't stop the timer.", err);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const totalMinutes = toTotalMinutes(formData.hours, formData.minutes);
      await timeEntriesApi.create({
        matterId: formData.matterId,
        clientId: formData.clientId,
        userId: formData.userId || currentUserId,
        date: formData.date,
        minutes: totalMinutes,
        narrative: formData.narrative,
        billable: formData.billable,
        rateCents: formData.rateCents,
      });
      setShowCreateModal(false);
      fetchEntries();
      fetchWeekEntries();
      onDataChange?.();
      notifySuccess("Time entry saved.");
    } catch (err) {
      notifyError("Couldn't save the time entry.", err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this time entry?")) return;
    try {
      await timeEntriesApi.remove(id);
      fetchEntries();
      onDataChange?.();
    } catch (err) {
      notifyError("Couldn't delete the time entry.", err);
    }
  };

  const handleWriteOff = async (id: string) => {
    if (!window.confirm("Write off this time entry?")) return;
    try {
      await timeEntriesApi.writeOff(id);
      fetchEntries();
      onDataChange?.();
    } catch (err) {
      notifyError("Couldn't write off the time entry.", err);
    }
  };

  const handleEditSave = async () => {
    if (!editingEntry) return;
    try {
      await timeEntriesApi.update(editingEntry._id, {
        narrative: editForm.narrative,
        minutes: editForm.minutes,
        rateCents: editForm.rateCents,
      });
      setEditingEntry(null);
      fetchEntries();
      onDataChange?.();
    } catch (err) {
      notifyError("Couldn't save your changes.", err);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleMatterChange = (matterId: string) => {
    const matter = matters.find((m: any) => m._id === matterId);
    const clientId = typeof matter?.clientId === "object" ? matter.clientId._id : matter?.clientId || "";
    setFormData((prev) => ({ ...prev, matterId, clientId }));
  };

  // AI Narrative improvement
  const handleImproveNarrative = async () => {
    if (!formData.narrative.trim()) return;
    setImprovingNarrative(true);
    setNarrativeImprovement(null);
    try {
      const totalMinutes = toTotalMinutes(formData.hours, formData.minutes);
      const res = await timeEntriesApi.improveNarrative({
        narrative: formData.narrative,
        matterId: formData.matterId || undefined,
        durationMinutes: totalMinutes > 0 ? totalMinutes : undefined,
      });
      setNarrativeImprovement(res.data);
    } catch (err) {
      console.error("Failed to improve narrative:", err);
    } finally {
      setImprovingNarrative(false);
    }
  };

  const applyImprovedNarrative = () => {
    if (narrativeImprovement) {
      setFormData((prev) => ({ ...prev, narrative: narrativeImprovement.improved }));
      setNarrativeImprovement(null);
    }
  };

  // Handle creating time entry from activity
  const handleCreateFromActivity = (activity: any) => {
    const matter = matters.find(
      (m: any) => m._id === (activity.matterId?._id || activity.matterId)
    );
    const clientId =
      typeof matter?.clientId === "object"
        ? matter.clientId._id
        : matter?.clientId || "";

    setFormData({
      matterId: activity.matterId?._id || "",
      clientId,
      userId: currentUserId,
      date: toLocalDateString(new Date(activity.occurredAt)),
      minutes: activity.durationSecs ? Math.ceil(activity.durationSecs / 60) : 6,
      hours: "",
      narrative: activity.description || "",
      billable: true,
      rateCents: 25000,
    });
    setShowCreateModal(true);
  };

  // Group entries by date
  const groupedEntries = entries.reduce<Record<string, TimeEntry[]>>((acc, entry) => {
    const dateKey = new Date(entry.date).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(entry);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* AI Screen Capture */}
      <ScreenCaptureWidget
        onDataChange={() => {
          fetchEntries();
          fetchWeekEntries();
          onDataChange?.();
        }}
      />

      {/* Timer Widget */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4" style={{ color: "var(--color-primary)" }} />
          Timer
        </h3>
        {runningTimer ? (
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-3xl font-mono font-bold text-slate-800">
                {formatTimerDisplay(timerElapsed)}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {typeof runningTimer.matterId === "object"
                  ? runningTimer.matterId?.title
                  : "Running..."}{" "}
                 -  {runningTimer.narrative}
              </p>
            </div>
            <button
              onClick={handleStopTimer}
              disabled={timerBusy}
              className="flex items-center gap-2 px-5 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-colors disabled:opacity-50"
            >
              <Square className="h-4 w-4" />
              Stop
            </button>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <SearchableSelect
                options={matters.map((m: any) => ({ value: m._id, label: `${m.matterNumber}  -  ${m.title}` }))}
                value={timerMatter || null}
                onChange={(val) => setTimerMatter(val || "")}
                placeholder="Select matter..."
              />
            </div>
            <input
              type="text"
              placeholder="What are you working on?"
              value={timerNarrative}
              onChange={(e) => setTimerNarrative(e.target.value)}
              className="flex-1 rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
            <input
              type="number"
              placeholder="Rate ($/hr)"
              value={timerRate ? timerRate / 100 : ""}
              onChange={(e) => setTimerRate(Math.round(parseFloat(e.target.value || "0") * 100))}
              className="w-28 rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
            <button
              onClick={handleStartTimer}
              disabled={!timerMatter || timerBusy}
              className="flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
            >
              <Play className="h-4 w-4" />
              Start
            </button>
          </div>
        )}
      </div>

      {/* Weekly Timesheet */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
        {/* Header row */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <CalendarDays className="h-4 w-4" style={{ color: "var(--color-primary)" }} />
            Weekly Timesheet
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const d = new Date(weekStart);
                d.setDate(d.getDate() - 7);
                setWeekStart(d);
              }}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-xs font-medium text-slate-600 w-36 text-center">
              {weekStart.toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
              {" — "}
              {weekDays[6].toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}
            </span>
            <button
              onClick={() => {
                const d = new Date(weekStart);
                d.setDate(d.getDate() + 7);
                setWeekStart(d);
              }}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => {
                const d = new Date();
                const day = d.getDay();
                const diff = day === 0 ? -6 : 1 - day;
                d.setDate(d.getDate() + diff);
                d.setHours(0, 0, 0, 0);
                setWeekStart(d);
              }}
              className="text-xs font-medium px-2.5 py-1 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors"
            >
              Today
            </button>
          </div>
        </div>

        {/* Day columns */}
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map((day) => {
            const mins = minutesForDay(day);
            const h = Math.floor(mins / 60);
            const m = mins % 60;
            const isToday = day.toDateString() === new Date().toDateString();
            const isPast = day < new Date(new Date().setHours(0, 0, 0, 0));
            return (
              <div
                key={day.toISOString()}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-colors ${
                  isToday
                    ? "border-emerald-200 bg-emerald-50"
                    : "border-slate-100 bg-slate-50 hover:bg-slate-100"
                }`}
              >
                <span className={`text-xs font-semibold uppercase tracking-wide ${isToday ? "text-emerald-700" : "text-slate-500"}`}>
                  {day.toLocaleDateString("en-CA", { weekday: "short" })}
                </span>
                <span className={`text-sm font-bold ${isToday ? "text-emerald-700" : "text-slate-700"}`}>
                  {day.toLocaleDateString("en-CA", { day: "numeric" })}
                </span>
                <span className={`text-xs font-medium ${mins > 0 ? "text-slate-700" : "text-slate-300"}`}>
                  {mins > 0 ? (h > 0 ? `${h}h${m > 0 ? ` ${m}m` : ""}` : `${m}m`) : "—"}
                </span>
                <button
                  onClick={() => openAddForDay(day)}
                  className={`mt-0.5 p-1 rounded-lg transition-colors ${
                    isToday
                      ? "text-emerald-600 hover:bg-emerald-100"
                      : "text-slate-400 hover:text-slate-600 hover:bg-slate-200"
                  }`}
                  title={`Add time for ${day.toLocaleDateString("en-CA", { weekday: "long", month: "short", day: "numeric" })}`}
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Week total */}
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
          <span className="text-xs text-slate-500">Week total</span>
          {loadingWeek ? (
            <span className="text-xs text-slate-400">Loading...</span>
          ) : (
            <span className="text-sm font-bold text-slate-700">
              {Math.floor(totalWeekMinutes / 60)}h {totalWeekMinutes % 60}m
              {totalWeekMinutes > 0 && (
                <span className="text-xs font-normal text-slate-400 ml-1">
                  ({weekEntries.length} {weekEntries.length === 1 ? "entry" : "entries"})
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setFormData({
                matterId: "",
                clientId: "",
                userId: currentUserId,
                date: toLocalDateString(new Date()),
                minutes: 0,
                hours: "",
                narrative: "",
                billable: true,
                rateCents: 25000,
              });
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
          >
            <Plus className="h-4 w-4" />
            Manual Entry
          </button>
          {selectedIds.size > 0 && (
            <span className="text-sm text-slate-500">
              {selectedIds.size} selected
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === "list"
                  ? "bg-white shadow-sm text-slate-800"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <List className="h-4 w-4" />
              List
            </button>
            <button
              onClick={() => setViewMode("calendar")}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === "calendar"
                  ? "bg-white shadow-sm text-slate-800"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <CalendarDays className="h-4 w-4" />
              Calendar
            </button>
          </div>
          {viewMode === "list" && (
            <p className="text-sm text-slate-500">{pagination.total} entries</p>
          )}
        </div>
      </div>

      {viewMode === "list" && (
        <>
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <SearchableSelect
              options={matters.map((m: any) => ({ value: m._id, label: `${m.matterNumber}  -  ${m.title}` }))}
              value={filterMatter || null}
              onChange={(val) => {
                setFilterMatter(val || "");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              placeholder="All Matters"
              clearable
            />
          </div>
          <SearchableSelect
            options={users.map((u: any) => ({ value: u._id, label: u.name }))}
            value={filterUser || null}
            onChange={(val) => {
              setFilterUser(val || "");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            placeholder="All Users"
            clearable
          />
          <SearchableSelect
            options={[
              { value: "unbilled", label: "Unbilled" },
              { value: "billed", label: "Billed" },
              { value: "written_off", label: "Written Off" },
            ]}
            value={filterStatus || null}
            onChange={(val) => {
              setFilterStatus(val || "");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            placeholder="All Statuses"
            clearable
          />
          <input
            type="date"
            value={filterDateFrom}
            onChange={(e) => {
              setFilterDateFrom(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            placeholder="From"
          />
          <input
            type="date"
            value={filterDateTo}
            onChange={(e) => {
              setFilterDateTo(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            placeholder="To"
          />
        </div>
      </div>

      {/* Time Entries List  -  Grouped by Date */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : entries.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-20"
        >
          <div className="relative mb-6">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
            <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
              <Clock className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">No time entries yet</h3>
          <p className="text-sm text-slate-500 text-center max-w-md mb-6 leading-relaxed">
            Start tracking billable hours with the timer above, or create a manual entry to log time against a matter.
          </p>
          <button
            onClick={() => {
              setFormData({
                matterId: "",
                clientId: "",
                userId: currentUserId,
                date: toLocalDateString(new Date()),
                minutes: 0,
                hours: "",
                narrative: "",
                billable: true,
                rateCents: 25000,
              });
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
          >
            <Plus className="h-4 w-4" />
            Manual Entry
          </button>
        </motion.div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedEntries).map(([dateLabel, dateEntries]) => (
            <div key={dateLabel}>
              <h3 className="text-sm font-semibold text-slate-600 mb-2">{dateLabel}</h3>
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                {dateEntries.map((entry, i) => (
                  <motion.div
                    key={entry._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.03 }}
                    className={`flex items-center gap-4 px-5 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors ${
                      entry.status === "unbilled" ? "cursor-pointer" : ""
                    }`}
                  >
                    {/* Checkbox for unbilled */}
                    {entry.status === "unbilled" && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(entry._id)}
                        onChange={() => toggleSelect(entry._id)}
                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    )}
                    {entry.status !== "unbilled" && <div className="w-4" />}

                    {/* Entry info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">
                        {entry.narrative}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {typeof entry.matterId === "object"
                          ? `${entry.matterId?.matterNumber}  -  ${entry.matterId?.title}`
                          : "Matter"}{" "}
                        {" | "}
                        {typeof entry.userId === "object" ? entry.userId?.name : "User"}
                      </p>
                    </div>

                    {/* Billable */}
                    {!entry.billable && (
                      <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-1 rounded-full">
                        Non-billable
                      </span>
                    )}

                    {/* Duration */}
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-800">
                        {formatMinutes(entry.minutes)}
                      </p>
                      <p className="text-xs text-slate-500">
                        @ {formatCurrency(entry.rateCents)}/hr
                      </p>
                    </div>

                    {/* Amount */}
                    <div className="text-right w-24">
                      <p className="text-sm font-bold text-slate-800">
                        {formatCurrency(entry.amountCents)}
                      </p>
                    </div>

                    {/* Status badge */}
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        statusColors[entry.status] || "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {entry.status.replace("_", " ")}
                    </span>

                    {/* Actions */}
                    {entry.status === "unbilled" && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setEditingEntry(entry);
                            setEditForm({
                              narrative: entry.narrative,
                              minutes: entry.minutes,
                              rateCents: entry.rateCents,
                            });
                          }}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                          title="Edit"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleSplitEntry(entry)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                          title="Split entry"
                        >
                          <Scissors className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleWriteOff(entry._id)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-amber-600"
                          title="Write off"
                        >
                          <XCircle className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(entry._id)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          ))}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-slate-500">
                Page {pagination.page} of {pagination.pages}
              </p>
              <div className="flex gap-2">
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
        </>
      )}

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <TimeCalendarView
          weekStart={weekStart}
          setWeekStart={setWeekStart}
          entries={weekEntries}
          matters={matters}
          clients={clients}
          loading={loadingWeek}
          onRefresh={() => {
            fetchWeekEntries();
            fetchEntries();
            onDataChange?.();
          }}
          selectedEntry={selectedCalendarEntry}
          onSelectEntry={setSelectedCalendarEntry}
        />
      )}

      {/* Create Manual Entry Modal */}
      <PremiumModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="New Time Entry"
        icon={Clock}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate as any}
              disabled={
                creating ||
                !formData.matterId ||
                !formData.narrative ||
                toTotalMinutes(formData.hours, formData.minutes) <= 0
              }
              className="px-5 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {creating ? "Saving..." : "Save Entry"}
            </button>
          </div>
        }
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Matter *</label>
            <SearchableSelect
              options={matters.map((m: any) => ({ value: m._id, label: `${m.matterNumber}  -  ${m.title}` }))}
              value={formData.matterId || null}
              onChange={(val) => handleMatterChange(val || "")}
              placeholder="Select a matter"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Timekeeper</label>
            <SearchableSelect
              options={users.map((u: any) => ({
                value: u._id,
                label: u._id === currentUserId ? `${u.name} (you)` : u.name,
              }))}
              value={formData.userId || currentUserId || null}
              onChange={(val) => setFormData({ ...formData, userId: val || currentUserId })}
              placeholder="Select timekeeper"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
            <input
              type="date"
              required
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Narrative / Description *
            </label>
            <textarea
              required
              value={formData.narrative}
              onChange={(e) => {
                setFormData({ ...formData, narrative: e.target.value });
                setNarrativeImprovement(null);
              }}
              rows={3}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="Describe the work performed..."
            />

            {/* Improve with AI button */}
            {formData.narrative.trim().length > 5 && !narrativeImprovement && (
              <button
                type="button"
                onClick={handleImproveNarrative}
                disabled={improvingNarrative}
                className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 transition-colors disabled:opacity-50"
              >
                {improvingNarrative ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Sparkles className="w-3 h-3" />
                )}
                {improvingNarrative ? "Improving..." : "Improve with AI"}
              </button>
            )}

            {/* AI Improvement diff view */}
            <AnimatePresence>
              {narrativeImprovement && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 space-y-2"
                >
                  <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    AI Suggestion
                  </p>
                  <p className="text-sm text-slate-400 line-through">{formData.narrative}</p>
                  <p className="text-sm text-emerald-800 font-medium">{narrativeImprovement.improved}</p>
                  {narrativeImprovement.changes.length > 0 && (
                    <ul className="text-[11px] text-slate-500 space-y-0.5 mt-1">
                      {narrativeImprovement.changes.map((c, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-emerald-500 mt-px">-</span> {c}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={applyImprovedNarrative}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                    >
                      <Check className="w-3 h-3" />
                      Apply
                    </button>
                    <button
                      type="button"
                      onClick={() => setNarrativeImprovement(null)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg transition-colors"
                    >
                      Keep Original
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* AI matter suggestion when no matter is selected */}
            {!formData.matterId && formData.narrative.trim().length > 10 && (
              <AiMatterSuggestion
                content={formData.narrative}
                contentType="time_entry"
                onSelect={(id) => handleMatterChange(id)}
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Hours</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.hours}
                onChange={(e) => {
                  // Allow decimal hours like 1.3 (one decimal point, digits only)
                  const v = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
                  setFormData({ ...formData, hours: v });
                }}
                placeholder="0.0"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Minutes</label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.minutes || ""}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9]/g, "");
                  const mins = Math.min(parseInt(v) || 0, 59);
                  setFormData({ ...formData, minutes: mins });
                }}
                placeholder="0"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Hourly Rate ($/hr) *
            </label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={formData.rateCents ? formData.rateCents / 100 : ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  rateCents: Math.round(parseFloat(e.target.value || "0") * 100),
                })
              }
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="250.00"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="billable"
              checked={formData.billable}
              onChange={(e) => setFormData({ ...formData, billable: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <label htmlFor="billable" className="text-sm font-medium text-slate-700">
              Billable
            </label>
          </div>
        </form>
      </PremiumModal>

      {/* Edit Entry Modal */}
      <PremiumModal
        open={!!editingEntry}
        onClose={() => setEditingEntry(null)}
        title="Edit Entry"
        icon={Edit3}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setEditingEntry(null)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleEditSave}
              className="px-5 py-3 rounded-xl text-white text-sm font-bold"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              Save Changes
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Narrative</label>
            <textarea
              value={editForm.narrative}
              onChange={(e) => setEditForm({ ...editForm, narrative: e.target.value })}
              rows={3}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Minutes
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={editForm.minutes || ""}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, "");
                setEditForm({ ...editForm, minutes: parseInt(v) || 0 });
              }}
              placeholder="0"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Rate ($/hr)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={editForm.rateCents / 100}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  rateCents: Math.round(parseFloat(e.target.value || "0") * 100),
                })
              }
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
        </div>
      </PremiumModal>

      {splittingEntry && (
        <SplitEntryModal
          entry={splittingEntry}
          onClose={() => setSplittingEntry(null)}
          onSplit={() => {
            setSplittingEntry(null);
            fetchEntries();
            fetchWeekEntries();
            onDataChange?.();
          }}
        />
      )}
    </div>
  );
};

export default TrackTimeTab;
